<template>
  <b-card>
    <slot name="hint" />
    <b-card-title class="mb-0">
      Portfolio History
    </b-card-title>
    <div class="font-small-3 mb-1 text-muted">
      Product history chart showing evolution of portfolio by product status
    </div>
    <b-card-text>
      <div v-if="loading">
        <loading-container />
      </div>
      <div
        v-else
        class="position-relative"
      >
        <div
          v-if="noData"
          class="no-data-overlay"
        >
          <span class="text-muted">No data available</span>
        </div>
        <app-echart-line
          :key="chartKey"
          :option-data="chartData"
        />
      </div>
    </b-card-text>
  </b-card>
</template>

<script>
import {
  BCard, BCardTitle, BCardText,
} from 'bootstrap-vue'
import AppEchartLine from '@core/components/charts/echart/AppEchartLine.vue'
import useAppConfig from '@core/app-config/useAppConfig'
import LoadingContainer from '@/views/components/LoadingContainer.vue'

export default {
  components: {
    BCard,
    BCardTitle,
    BCardText,
    AppEchartLine,
    LoadingContainer,
  },
  props: {
    portfolioData: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { skin } = useAppConfig()
    return { skin }
  },
  data() {
    return {
      noData: false,
      chartKey: 0,
      chartData: {},
    }
  },
  computed: {
    splitLineOpacity() {
      return this.skin === 'dark' ? 0.1 : 1
    },
  },
  watch: {
    portfolioData: {
      handler(data) {
        this.updateChart(data)
      },
      immediate: true,
    },
    skin() {
      this.updateChart(this.portfolioData)
    },
  },
  methods: {
    updateChart(data) {
      const apexSeries = data?.portfoliochart?.SHOWDATA?.DATA
      if (!this.hasSeriesData(apexSeries)) {
        this.setNoData()
        return
      }

      try {
        this.processChartData(apexSeries)
      } catch (err) {
        console.error('Error processing portfolio history chart:', err)
        this.setNoData()
      }
    },
    hasSeriesData(apexSeries) {
      return Array.isArray(apexSeries) && apexSeries.some(series => (
        Array.isArray(series?.data) && series.data.some(point => point && point.x !== undefined && point.y !== undefined)
      ))
    },
    setNoData() {
      this.noData = true
      this.chartData = {
        tooltip: {},
        legend: { show: false },
        grid: {
          left: 70, right: 30, bottom: 50, top: 40,
        },
        xAxis: { type: 'time' },
        yAxis: { type: 'value' },
        series: [],
      }
      this.chartKey += 1
    },
    processChartData(apexSeries) {
      this.noData = false
      const colors = ['#ea5455', '#28c76f', '#1a73e8'] // pre-strike, live, matured

      const echartsSeries = apexSeries.map((series, index) => ({
        name: series.name,
        type: 'line',
        smooth: true,
        showSymbol: false,
        data: (series.data || [])
          .filter(point => point && point.x !== undefined && point.y !== undefined)
          .map(point => [point.x, point.y]),
        lineStyle: {
          width: 2,
          color: colors[index % colors.length],
        },
        itemStyle: {
          color: colors[index % colors.length],
        },
      }))

      this.chartData = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
          },
          formatter: params => {
            const date = new Date(params[0].value[0])
            const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
            let result = `${dateStr}<br/>`
            params.forEach(param => {
              const value = param.value[1].toFixed(2)
              result += `${param.marker} ${param.seriesName}: ${value}<br/>`
            })
            return result
          },
        },
        legend: {
          show: true,
          top: 0,
          icon: 'circle',
        },
        grid: {
          left: 70,
          right: 30,
          bottom: 50,
          top: 40,
          containLabel: false,
        },
        xAxis: {
          type: 'time',
          name: 'Date',
          nameLocation: 'middle',
          nameGap: 35,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
            showMinLabel: false,
            showMaxLabel: false,
            formatter(value) {
              const date = new Date(value)
              return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
            },
          },
          splitLine: {
            show: false,
          },
        },
        yAxis: {
          type: 'value',
          name: 'Value',
          nameLocation: 'middle',
          nameGap: 50,
          min: 0,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
            formatter: value => value.toFixed(2),
          },
          splitLine: {
            lineStyle: {
              opacity: this.splitLineOpacity,
              type: 'dashed',
            },
          },
        },
        series: echartsSeries,
      }

      this.chartKey += 1
    },
  },
}
</script>

<style lang="scss" scoped>
.no-data-overlay {
  position: absolute;
  top: 45%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  font-size: 1.1rem;
}
</style>
